import {IonIcon, IonRouterLink} from "@ionic/react";
import {
  arrowRedoOutline,
  calendarNumberOutline,
  locationOutline,
  mailOutline,
  personOutline,
  readerOutline,
  swapHorizontalOutline
} from "ionicons/icons";
import {styled} from "styled-components";
import type {TicketDetailFragment, UserFragment} from "app/gql/graphqlSchema";
import {linkEmplControlCardInstance} from "app/employee/core/employeeLinks";
import {isNotNil} from "app/utils/stdlib";
import {usePermissions} from "app/employee/hooks/usePermissions";
import {ticketItemColor, ticketStatusIcon} from "app/employee/ticket/ticketStatusIcon";
import {IonRowButton} from "app/employee/IonRowButton";
import {AttachmentCollection} from "app/employee/att/AttachmentCollection";
import {Trans, useTranslation} from "react-i18next";
import {DateDisplay} from "app/employee/DateDisplay";
import {Timestamp} from "app/employee/Timestamp";
import {StatusBadge} from "app/employee/ticket/StatusBadge";
import {getLocationText} from "app/employee/utils";

const SummaryPanel = styled.div`
  border: 1px solid var(--fst-border-color);
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: #f9f9f9;
  margin: 0.5rem;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
`;

const SummaryHeader = styled.div`
  padding: 0.75rem 0.75rem 0.25rem;
  font-weight: 600;
`;

const SummaryTable = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.5rem;
  margin: 0.5rem;
`;

const SummaryContent = styled.div`
  border-top: 1px solid var(--fst-border-color);
  padding: 0.5rem;
  background-color: var(--ion-color-highlight);
  color: var(--ion-color-highlight-contrast);
`;

const SummaryAttachments = styled.div`
  background-color: var(--ion-color-highlight);
  color: var(--ion-color-highlight-contrast);
  padding: 0.5rem;
`;

const StatusSection = styled.div<{$color: string}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid var(--fst-border-color);
  padding: 0.5rem;
  gap: 0.5rem;
  background-color: var(--ion-color-${(p) => p.$color});
  color: var(--ion-color-${(p) => p.$color}-contrast);
`;

const StatusInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DoneByGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.125rem 0.5rem;
  align-items: center;
`;

const getUserName = (user: UserFragment) => user.name;

const ControlCardLink = ({
  reference,
  canView
}: {
  reference: NonNullable<TicketDetailFragment["controlCardReference"]>;
  canView: boolean;
}) => {
  const {t} = useTranslation();
  const instanceId = reference.instance.instanceId.instanceId;
  const rowId = reference.instance.isTransposed ? undefined : reference.rowInstanceId;
  const href = linkEmplControlCardInstance(instanceId, rowId);
  const name = canView ? (
    <IonRouterLink routerLink={href}>{reference.instance.name}</IonRouterLink>
  ) : (
    reference.instance.name
  );
  return (
    <>
      <div>
        <IonIcon icon={readerOutline} />
      </div>
      <div>
        <Trans
          components={{Name: <>{name}</>, Date: <DateDisplay dt={reference.instance.date} />}}
          i18nKey="ticket.detail.control-card"
          t={t}
        />
      </div>
    </>
  );
};

interface Props {
  ticket: TicketDetailFragment;
  canEdit: boolean;
  onAttachmentChanged?: () => void;
  onChangeStatus?: () => void;
}

export const TicketSummary = ({ticket, canEdit, onAttachmentChanged, onChangeStatus}: Props) => {
  const {t} = useTranslation();
  const {canViewControlCardLocation} = usePermissions();
  const recipientNames = ticket.recipients.map((r) => getUserName(r.recipient)).join(", ");
  const showAttachments = !(!canEdit && ticket.attachmentCollection.attachments.length === 0);
  return (
    <SummaryPanel>
      <SummaryHeader>{ticket.subject}</SummaryHeader>
      <SummaryTable>
        <div>
          <IonIcon icon={arrowRedoOutline} />
        </div>
        <div>{getUserName(ticket.sender)}</div>
        <div>
          <IonIcon icon={mailOutline} />
        </div>
        <div>{recipientNames}</div>
        <div>
          <IonIcon icon={calendarNumberOutline} />
        </div>
        <div>
          <Timestamp ts={ticket.createdAt} />
        </div>
        {ticket.locations.length > 0 && (
          <>
            <div>
              <IonIcon icon={locationOutline} />
            </div>
            <div>{ticket.locations.map(getLocationText).join(", ")}</div>
          </>
        )}
        {isNotNil(ticket.controlCardReference) && (
          <ControlCardLink
            canView={canViewControlCardLocation(ticket.controlCardReference.instance.location.id)}
            reference={ticket.controlCardReference}
          />
        )}
      </SummaryTable>
      <StatusSection $color={ticketItemColor(ticket.status)}>
        <StatusInfo>
          <div>
            <StatusBadge>
              <IonIcon icon={ticketStatusIcon[ticket.status]} /> {t(`ticket.status.${ticket.status}`)}
            </StatusBadge>
          </div>
          {ticket.statusChangedBy && ticket.statusChangedAt && (
            <DoneByGrid>
              <IonIcon icon={personOutline} />
              <span>{getUserName(ticket.statusChangedBy)}</span>
              <IonIcon icon={calendarNumberOutline} />
              <span>
                <Timestamp ts={ticket.statusChangedAt} />
              </span>
            </DoneByGrid>
          )}
        </StatusInfo>
        {onChangeStatus && (
          <IonRowButton onClick={onChangeStatus}>
            <IonIcon icon={swapHorizontalOutline} slot="start" />
            {t("g.change")}
          </IonRowButton>
        )}
      </StatusSection>
      {ticket.content && <SummaryContent>{ticket.content}</SummaryContent>}
      {showAttachments && (
        <SummaryAttachments>
          <AttachmentCollection
            attCollection={ticket.attachmentCollection}
            canDelete={canEdit}
            canUpload={canEdit}
            name={ticket.subject}
            onDeleteCompleted={onAttachmentChanged}
            onUploadCompleted={onAttachmentChanged}
          />
        </SummaryAttachments>
      )}
    </SummaryPanel>
  );
};
